import {
  Button,
  FormField,
  Grid,
  Header,
  Input,
  NonCancelableCustomEvent,
  SpaceBetween,
  TokenGroup,
  TokenGroupProps,
} from '@amzn/awsui-components-react-v3';
import Container from '@amzn/awsui-components-react-v3/polaris/container/internal';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import ScanInput from '../../../../components/ScanInput/index-v3';
import { nonUpdatableFormFields } from '../config';
import { FormikState } from './schema';

export default function ModelInformation({
  form,
  isEditForm,
}: {
  form: FormikState;
  isEditForm: boolean;
}): ReactElement {
  const { t } = useTranslation();

  const handleAddSerial = () => {
    if (!form.values.serial || form.errors.serial) {
      return;
    }
    if (form.values.serials.every((i: TokenGroupProps.Item) => i.label !== form.values.serial)) {
      form.setFieldValue('serials', [{ label: form.values.serial }, ...form.values.serials]);
    }
    form.setFieldValue('serial', '');
  };

  const handleRemoveSerial = (event: NonCancelableCustomEvent<TokenGroupProps.DismissDetail>) => {
    const [...serials] = form.values.serials;
    serials.splice(event.detail.itemIndex, 1);
    form.setFieldValue('serials', serials);
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description=""
          actions={
            <span>
              <i className="red-text">*</i> {t('Required field')}
            </span>
          }>
          {t('Model Information')}
        </Header>
      }>
      <SpaceBetween direction="vertical" size="m">
        <FormField
          description=""
          label={t('Holding Container')}
          errorText={form.touched.holdingLocation && form.errors.holdingLocation}>
          <Input
            data-test-id="input-holding-location"
            value={form.values.holdingLocation}
            onBlur={() => {
              form.setFieldTouched('holdingLocation');
            }}
            onChange={(event) => {
              form.setFieldValue('holdingLocation', event.detail.value);
            }}
            placeholder={t('Scan holding container information')}
          />
        </FormField>
        <FormField
          description=""
          label={t('Model')}
          errorText={form.touched.model && form.errors.model}>
          <Input
            disabled={isEditForm && nonUpdatableFormFields.includes('model')}
            data-test-id="input-model"
            value={form.values.model}
            onBlur={() => {
              form.setFieldTouched('model');
            }}
            onChange={(event) => {
              form.setFieldValue('model', event.detail.value);
            }}
            placeholder={t('Enter model number')}
          />
        </FormField>
        <FormField
          description=""
          label={t('APN or IPN')}
          errorText={form.touched.legacyModelId && form.errors.legacyModelId}>
          <Input
            disabled={isEditForm && nonUpdatableFormFields.includes('legacyModelId')}
            data-test-id="input-legacy-model-id"
            value={form.values.legacyModelId}
            onBlur={() => {
              form.setFieldTouched('legacyModelId');
            }}
            onChange={(event) => {
              form.setFieldValue('legacyModelId', event.detail.value);
            }}
            placeholder={t('Enter APN or IPN')}
          />
        </FormField>
        <FormField
          description=""
          label={t('Serial numbers')}
          errorText={form.touched.serial && form.errors.serial}>
          <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
            <div>
              <ScanInput
                data-test-id="input-serial-number"
                onInput={(value) => {
                  form.setFieldValue('serial', value);
                  form.setFieldTouched('serial');
                }}
                onEnter={handleAddSerial}
                clearOnEnter
                value={form.values.serial}
                placeholder={t('Scan or Enter Serial Number')}
              />
            </div>
            <div>
              <Button variant="normal" onClick={handleAddSerial} data-test-id="button-add-serial">
                {t('Enter serial')}
              </Button>
            </div>
          </Grid>
        </FormField>
        <TokenGroup
          data-test-id="list-serials"
          onDismiss={handleRemoveSerial}
          items={form.values.serials}
        />
        <FormField
          description=""
          label={t('Quantity')}
          errorText={form.touched.quantity && form.errors.quantity}>
          <Input
            type="number"
            data-test-id="input-quantity"
            placeholder="0"
            value={`${form.values.quantity}`}
            onBlur={() => {
              form.setFieldTouched('quantity');
            }}
            onChange={(event) => {
              form.setFieldValue('quantity', event.detail.value);
            }}
          />
        </FormField>
      </SpaceBetween>
    </Container>
  );
}
